import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Trophy, DollarSign, TrendingDown, Sparkles, ArrowLeft, Package, Loader2, ChevronRight, Download, Target, Check, X as XIcon, Square, CheckSquare,
} from 'lucide-react';
import ProductDetailModal from '@/components/products/ProductDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n || 0);
const fmtUnit = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const PERIODS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const ProductAnalyticsPage = () => {
  const [topProducts, setTopProducts] = useState([]);
  const [savings, setSavings] = useState({ total_potential_savings: 0, opportunities: [] });
  const [period, setPeriod] = useState('90d');
  const [loading, setLoading] = useState(true);
  const [savingsLoading, setSavingsLoading] = useState(true);
  const [activeProductId, setActiveProductId] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [onWaitlist, setOnWaitlist] = useState(false);

  // Bulk target-price editing
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkTargetInput, setBulkTargetInput] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Load top products (re-fetch when period changes)
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/products/top?period=${period}`, { withCredentials: true })
      .then((r) => setTopProducts(r.data || []))
      .catch(() => setTopProducts([]))
      .finally(() => setLoading(false));
  }, [period]);

  // Load savings once
  useEffect(() => {
    setSavingsLoading(true);
    axios.get(`${API}/analytics/products/savings`, { withCredentials: true })
      .then((r) => setSavings(r.data || { total_potential_savings: 0, opportunities: [] }))
      .catch(() => setSavings({ total_potential_savings: 0, opportunities: [] }))
      .finally(() => setSavingsLoading(false));
  }, []);

  // Waitlist status (best-effort)
  useEffect(() => {
    axios.get(`${API}/waitlist/status`, { withCredentials: true })
      .then((r) => setOnWaitlist(!!r.data?.joined))
      .catch(() => {});
  }, []);

  const joinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await axios.post(`${API}/waitlist/join`, {
        source: 'product_analytics_savings',
        context: { savings_amount: savings.total_potential_savings },
      }, { withCredentials: true });
      setOnWaitlist(true);
      toast.success("You're on the waitlist! We'll notify you when the Marketplace launches.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const downloadSavings = async (format) => {
    try {
      const res = await axios.get(
        `${API}/analytics/products/savings/export?format=${format}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `savings_opportunities_${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  const toggleSelect = (pid) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setSelected(new Set());
    setBulkTargetInput('');
  };

  const applyBulkTargets = async (clear = false) => {
    if (selected.size === 0) {
      toast.error('Select at least one product');
      return;
    }
    const target_price = clear ? null : parseFloat(bulkTargetInput);
    if (!clear && (Number.isNaN(target_price) || target_price < 0)) {
      toast.error('Enter a valid target price');
      return;
    }
    setBulkSaving(true);
    try {
      const body = {
        targets: Array.from(selected).map((pid) => ({ product_id: pid, target_price })),
      };
      const res = await axios.put(`${API}/products/targets/bulk`, body, { withCredentials: true });
      const { updated, cleared } = res.data;
      toast.success(clear
        ? `Cleared ${cleared} target${cleared !== 1 ? 's' : ''}`
        : `Set target on ${updated} product${updated !== 1 ? 's' : ''}`);
      exitBulkMode();
      // Re-fetch top products so the column reflects new targets
      const tp = await axios.get(`${API}/analytics/products/top?period=${period}`, { withCredentials: true });
      setTopProducts(tp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update targets');
    } finally {
      setBulkSaving(false);
    }
  };

  const topSpendTotal = useMemo(
    () => topProducts.reduce((s, p) => s + (p.total_spent || 0), 0),
    [topProducts],
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" data-testid="back-to-dashboard" className="h-10 w-10 flex items-center justify-center hover:bg-zinc-100">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-heading font-bold text-2xl uppercase tracking-wide">Product Analytics</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Pricing intelligence powered by your catalog-linked line items.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {/* SAVINGS HEADLINE */}
        <div
          className="bg-primary border-2 border-black p-6 sm:p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          data-testid="savings-headline"
        >
          <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center justify-between">
            <div>
              <p className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-900/70 mb-1">
                Money Left on the Table
              </p>
              <p className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="total-savings-amount">
                {fmtCurrency(savings.total_potential_savings)}
              </p>
              <p className="text-sm text-zinc-900/80 mt-2">
                Potential savings if you'd bought every product at your lowest-known vendor price (trailing 90 days).
              </p>
            </div>
            {!onWaitlist ? (
              <button
                onClick={joinWaitlist}
                disabled={joiningWaitlist || savings.total_potential_savings <= 0}
                data-testid="join-marketplace-waitlist"
                className="h-14 px-6 bg-black text-primary font-heading font-bold uppercase tracking-wider border-2 border-black hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                <Sparkles className="w-5 h-5" />
                Join Marketplace Waitlist
              </button>
            ) : (
              <div className="h-14 px-6 bg-emerald-600 text-white font-heading font-bold uppercase tracking-wider border-2 border-black flex items-center gap-2 whitespace-nowrap">
                <Sparkles className="w-5 h-5" />
                You're on the list!
              </div>
            )}
          </div>
        </div>

        {/* SAVINGS OPPORTUNITIES */}
        <section>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-emerald-600" />
              <h2 className="font-heading font-bold text-lg uppercase tracking-wide">Savings Opportunities</h2>
            </div>
            <div className="flex items-center gap-2">
              {savings.opportunities.length > 0 && (
                <>
                  <button
                    onClick={() => downloadSavings('csv')}
                    data-testid="savings-export-csv"
                    className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase tracking-wider hover:bg-zinc-50 flex items-center gap-1.5"
                    title="Download as CSV"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                  <button
                    onClick={() => downloadSavings('pdf')}
                    data-testid="savings-export-pdf"
                    className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase tracking-wider hover:bg-zinc-50 flex items-center gap-1.5"
                    title="Download as PDF"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                </>
              )}
              <p className="text-xs text-zinc-500 hidden md:block">Products bought from 2+ vendors at different prices.</p>
            </div>
          </div>
          {savingsLoading ? (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
            </div>
          ) : savings.opportunities.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="savings-empty">
              <Trophy className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
              <p className="font-bold text-zinc-700">No price variance detected yet</p>
              <p className="text-sm text-zinc-500 mt-1">
                Order the same product from at least 2 vendors to surface savings opportunities.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white border-2 border-black">
              <table className="w-full min-w-[760px] text-sm" data-testid="savings-table">
                <thead>
                  <tr className="bg-zinc-900 text-white">
                    <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Product</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">You Pay (avg)</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Best Price</th>
                    <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Best Vendor</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Qty (90d)</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Potential Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {savings.opportunities.map((o) => (
                    <tr
                      key={o.product_id}
                      onClick={() => setActiveProductId(o.product_id)}
                      data-testid={`savings-row-${o.product_id}`}
                      className="border-b border-zinc-100 hover:bg-yellow-50 cursor-pointer"
                    >
                      <td className="px-3 py-3">
                        <p className="font-medium text-zinc-900">{o.product_name}</p>
                        {o.category && (
                          <span className="inline-block mt-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-600">
                            {o.category}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCurrency(o.current_avg_price)}</td>
                      <td className="px-3 py-3 text-right font-mono text-emerald-700 font-bold">{fmtCurrency(o.best_available_price)}</td>
                      <td className="px-3 py-3 font-medium text-emerald-700">{o.best_vendor}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtUnit(o.quantity_last_90d)}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-zinc-900">
                        {fmtCurrency(o.potential_savings)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* TOP PRODUCTS BY SPEND */}
        <section>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              <h2 className="font-heading font-bold text-lg uppercase tracking-wide">Top Products by Spend</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                disabled={bulkMode}
                data-testid="top-products-period"
                className="h-10 border-2 border-black bg-white text-xs font-bold uppercase px-3 focus:outline-none disabled:opacity-50"
              >
                {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              {!bulkMode ? (
                <button
                  onClick={() => setBulkMode(true)}
                  disabled={topProducts.length === 0}
                  data-testid="bulk-edit-targets-btn"
                  className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase tracking-wider hover:bg-zinc-50 flex items-center gap-1.5 disabled:opacity-40"
                  title="Set target prices for multiple products"
                >
                  <Target className="w-3.5 h-3.5" /> Bulk Edit Targets
                </button>
              ) : (
                <button
                  onClick={exitBulkMode}
                  data-testid="bulk-edit-cancel"
                  className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase tracking-wider hover:bg-zinc-50 flex items-center gap-1.5"
                >
                  <XIcon className="w-3.5 h-3.5" /> Cancel
                </button>
              )}
              <span className="text-xs text-zinc-500 hidden sm:inline">
                Total: <span className="font-bold text-zinc-900">{fmtCurrency(topSpendTotal)}</span>
              </span>
            </div>
          </div>

          {loading ? (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
            </div>
          ) : topProducts.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="top-products-empty">
              <Package className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
              <p className="font-bold text-zinc-700">No catalog-linked spend in this period</p>
              <p className="text-sm text-zinc-500 mt-1">Link line items to catalog products to populate this view.</p>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white border-2 border-black">
              <table className="w-full min-w-[760px] text-sm" data-testid="top-products-table">
                <thead>
                  <tr className="bg-zinc-900 text-white">
                    {bulkMode && (
                      <th className="px-2 py-2 w-10 text-center">
                        <button
                          onClick={() => {
                            if (selected.size === topProducts.length) setSelected(new Set());
                            else setSelected(new Set(topProducts.map(p => p.product_id)));
                          }}
                          data-testid="bulk-select-all"
                          className="h-6 w-6 inline-flex items-center justify-center"
                          title={selected.size === topProducts.length ? 'Clear all' : 'Select all'}
                        >
                          {selected.size === topProducts.length
                            ? <CheckSquare className="w-4 h-4 text-primary" />
                            : <Square className="w-4 h-4 text-white" />}
                        </button>
                      </th>
                    )}
                    <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">#</th>
                    <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Product</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Total Spent</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Qty</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Vendors</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Avg Price</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Target</th>
                    <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider">Variance</th>
                    {!bulkMode && <th className="px-2 py-2 w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, idx) => {
                    const isSelected = selected.has(p.product_id);
                    return (
                      <tr
                        key={p.product_id}
                        onClick={() => bulkMode ? toggleSelect(p.product_id) : setActiveProductId(p.product_id)}
                        data-testid={`top-product-row-${p.product_id}`}
                        className={`border-b border-zinc-100 cursor-pointer ${isSelected ? 'bg-yellow-100' : 'hover:bg-yellow-50'}`}
                      >
                        {bulkMode && (
                          <td className="px-2 py-3 text-center">
                            {isSelected
                              ? <CheckSquare className="w-4 h-4 text-zinc-900 inline" data-testid={`bulk-row-checked-${p.product_id}`} />
                              : <Square className="w-4 h-4 text-zinc-400 inline" />}
                          </td>
                        )}
                        <td className="px-3 py-3 font-mono text-zinc-400 text-xs">{idx + 1}</td>
                        <td className="px-3 py-3">
                          <p className="font-medium text-zinc-900">{p.product_name}</p>
                          {p.category && (
                            <span className="inline-block mt-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-600">
                              {p.category}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold">{fmtCurrency(p.total_spent)}</td>
                        <td className="px-3 py-3 text-right font-mono">{fmtUnit(p.total_quantity)}</td>
                        <td className="px-3 py-3 text-right font-mono">{p.vendor_count}</td>
                        <td className="px-3 py-3 text-right font-mono">{fmtCurrency(p.average_unit_price)}</td>
                        <td className={`px-3 py-3 text-right font-mono text-xs ${p.above_target ? 'text-red-600 font-bold' : 'text-zinc-500'}`} data-testid={`top-product-target-${p.product_id}`}>
                          {p.target_price != null ? (
                            <span className="inline-flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {fmtCurrency(p.target_price)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className={`px-3 py-3 text-right font-mono ${p.price_variance > 0 ? 'text-orange-600' : 'text-zinc-400'}`}>
                          {p.price_variance > 0 ? fmtCurrency(p.price_variance) : '—'}
                        </td>
                        {!bulkMode && (
                          <td className="px-2 py-3 text-zinc-400">
                            <ChevronRight className="w-4 h-4" />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ProductDetailModal
        productId={activeProductId}
        open={!!activeProductId}
        onClose={() => setActiveProductId(null)}
      />

      {/* Bulk-edit sticky action bar */}
      {bulkMode && (
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 text-white border-t-4 border-primary shadow-[0_-4px_0_0_rgba(0,0,0,1)] z-40" data-testid="bulk-edit-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              <span className="font-heading font-bold text-sm uppercase tracking-wider" data-testid="bulk-selected-count">
                {selected.size} selected
              </span>
            </div>
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
              <span className="text-xs font-mono">Target price:</span>
              <div className="flex items-center gap-1">
                <span className="font-mono">$</span>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={bulkTargetInput}
                  onChange={(e) => setBulkTargetInput(e.target.value)}
                  placeholder="0.00"
                  data-testid="bulk-target-input"
                  className="w-28 h-10 border-2 border-primary bg-zinc-900 text-white px-2 font-mono focus:outline-none focus:bg-zinc-800"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => applyBulkTargets(true)}
                disabled={selected.size === 0 || bulkSaving}
                data-testid="bulk-clear-targets"
                className="h-10 px-3 bg-zinc-700 border-2 border-zinc-500 text-xs font-heading font-bold uppercase tracking-wider hover:bg-zinc-600 disabled:opacity-40"
                title="Clear targets on selected products"
              >
                Clear
              </button>
              <button
                onClick={() => applyBulkTargets(false)}
                disabled={selected.size === 0 || !bulkTargetInput || bulkSaving}
                data-testid="bulk-apply-targets"
                className="h-10 px-4 bg-primary text-zinc-900 border-2 border-primary text-xs font-heading font-bold uppercase tracking-wider hover:bg-primary-hover flex items-center gap-1 disabled:opacity-40"
              >
                {bulkSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Apply</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAnalyticsPage;
